import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { prefetchHandlers } from '@/lib/routePrefetch';

interface NavLinkItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

interface AdminNavDropdownProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  links: NavLinkItem[];
  isExpanded: boolean;
  onToggle: () => void;
  currentPath: string;
  onLinkClick?: () => void;
}

export const AdminNavDropdown: React.FC<AdminNavDropdownProps> = ({
  title,
  icon: Icon,
  links,
  isExpanded,
  onToggle,
  currentPath,
  onLinkClick,
}) => {
  const hasActiveLink = links.some((link) => currentPath === link.href);

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={cn(
          "flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-colors",
          hasActiveLink
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <span className="font-medium">{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="ml-4 mt-1 space-y-1 border-l border-border pl-2">
              {links.map((link, index) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03, duration: 0.15 }}
                >
                  <Link
                    to={link.href}
                    onClick={onLinkClick}
                    {...prefetchHandlers(link.href)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
                      currentPath === link.href
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <link.icon className="w-4 h-4" />
                    <span>{link.name}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
